import * as React from 'react';

/**
 * Opt-in portal scoping for the shadcn/Radix overlay primitives.
 *
 * WHY (creator-shortlist-refinements-v2 Phase 6, U5): Radix portals its overlay
 * content to `document.body` by default. The branded magic-link shells theme
 * themselves through variables scoped to `.branded-shell`, so a dialog/sheet/
 * popover/select/dropdown/tooltip rendered into `body` sits OUTSIDE the themed
 * subtree and cannot inherit the shell's font, colours, focus ring or shadcn
 * bridge. `BrandedThemeProvider` renders one stable host element inside its
 * themed root and publishes it here; the seven primitives read it and portal
 * into it instead.
 *
 * DEFAULT IS UNCHANGED BEHAVIOUR. There is no default container: outside a
 * `ScopedPortalProvider` the context is `undefined`, `useScopedPortalProps()`
 * omits `container`, and Radix falls back to `document.body` exactly as before.
 * The four role portals render no provider, so nothing about them changes.
 *
 * No document-wide selector and no body mutation is involved — the host is a
 * plain DOM descendant of the branded shell.
 */

/** The container shape Radix's `Portal` accepts. */
export type ScopedPortalContainer = Element | DocumentFragment | null;

interface ScopedPortalContextValue {
  /** `null` while the provider is scoped but its host element has not mounted yet. */
  container: ScopedPortalContainer;
}

const ScopedPortalContext = React.createContext<ScopedPortalContextValue | undefined>(undefined);

export interface ScopedPortalProviderProps {
  /**
   * The element portalled overlay content should be appended to. Pass `null`
   * while the host is still mounting — consumers then render nothing rather
   * than mounting into `body` and reparenting afterwards.
   */
  container: ScopedPortalContainer | undefined;
  children: React.ReactNode;
}

export function ScopedPortalProvider({ container, children }: ScopedPortalProviderProps) {
  const value = React.useMemo<ScopedPortalContextValue>(
    () => ({ container: container ?? null }),
    [container],
  );
  return <ScopedPortalContext.Provider value={value}>{children}</ScopedPortalContext.Provider>;
}

/**
 * Low-level read of the scope.
 *
 * - `scoped: false` — no provider above this component (every non-branded
 *   caller). Keep Radix's default `document.body` portal.
 * - `scoped: true, container: null` — scoped, but the host has not mounted yet.
 * - `scoped: true, container: <el>` — portal into `<el>`.
 */
export function useScopedPortalContainer(): { scoped: boolean; container: ScopedPortalContainer } {
  const context = React.useContext(ScopedPortalContext);
  return context
    ? { scoped: true, container: context.container }
    : { scoped: false, container: null };
}

export interface ScopedPortalProps {
  /**
   * Spread onto the primitive's Radix `Portal`. `undefined` means "no container
   * prop" i.e. Radix's default `document.body`.
   */
  container?: ScopedPortalContainer;
  /**
   * `false` only when the scope is active but its host has not mounted yet.
   * The primitive must render NOTHING for its portalled content in that frame,
   * so overlay content never mounts into `body` first and then reparents
   * (which would remount the content and drop focus/scroll-lock state).
   */
  ready: boolean;
  /**
   * `true` when the portalled content is being targeted at an explicit
   * container (caller-supplied or scoped host). Used by `tooltip`, whose
   * content is NOT portalled by default — it only introduces a Portal when a
   * container is actually being targeted.
   */
  targeted: boolean;
}

/**
 * The single helper every container-aware primitive uses.
 *
 * PRECEDENCE (documented contract): a caller-supplied `container` prop always
 * wins, including an explicit `null` (which means "Radix default: body") — a
 * caller that names a container is never made to wait on, or be overridden by,
 * the branded host. `undefined` (the default, i.e. the prop was not passed)
 * falls through to the scoped host, and with no scope at all to Radix's own
 * default.
 */
export function useScopedPortalProps(callerContainer?: ScopedPortalContainer): ScopedPortalProps {
  const { scoped, container } = useScopedPortalContainer();

  if (callerContainer !== undefined) {
    return { container: callerContainer, ready: true, targeted: callerContainer !== null };
  }
  if (!scoped) return { ready: true, targeted: false };
  if (container) return { container, ready: true, targeted: true };
  return { ready: false, targeted: true };
}
